import React, { useCallback, useEffect, useMemo, useState } from "react";
import { filterByServiceDomain, isAmpEntitlementDomain } from "../../constants/entitlementDomains";
import PermissionObjectTable from "./PermissionObjectTable";
import PermissionObjectForm, { PoPayload, UsedBy } from "./PermissionObjectForm";
import Dialog from "../../FormDialog/Dialog";
import ErrorDialog from "../../ErrorDialog/ErrorDialog";
import { getUserInfo } from "../../../../services/authService-sighup";
import { getHeaders } from "../../../ApiHeaders/apiHeadersEntitlements";
import { SplitDivider, useResizableSplit } from "../../../SplitPane/ResizableSplit";
import LoadingStyle from "../../../Loading/Loading";
import {
  getMethodAccess,
  putMethodAccess,
} from "../../api/entitlementsAdminApi";
import { formatEntitlementsError } from "../../api/entitlementsMessages";
import {
  MethodAccessGridValue,
  defaultMethodAccess,
  fromEntries,
  toEntries,
} from "./permissionObjectUtils";

const apiUrl = process.env.REACT_APP_ENTITLEMENTS_API;

interface Props {
  registerTabSwitchGuard?: (guard: () => boolean | Promise<boolean>) => void;
  canDelete?: boolean;
}

const PermissionObjectSetup: React.FC<Props> = ({ registerTabSwitchGuard, canDelete = false }) => {
  const [pos, setPos] = useState<any[]>([]);
  const [serviceList, setServiceList] = useState<any[] | null>(null);
  const [pogs, setPogs] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [roleMappings, setRoleMappings] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [methodAccess, setMethodAccess] = useState<MethodAccessGridValue | null>(null);
  const [poGroupIds, setPoGroupIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit" | "view" | null>(null);
  const [error, setError] = useState<{ title: string; details: string } | null>(null);
  const [dialog, setDialog] = useState<{
    open: boolean;
    onConfirm: () => void;
    onCancel: () => void;
  } | null>(null);

  const actor = getUserInfo()?.email || "system";

  const getJson = useCallback(async (path: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${apiUrl}${path}`, { headers });
    if (!res.ok) throw new Error(`GET ${path} failed (${res.status})`);
    const json = await res.json();
    return json.response?.data ?? [];
  }, []);

  const send = useCallback(async (method: string, path: string, body?: unknown) => {
    const headers = await getHeaders();
    const res = await fetch(`${apiUrl}${path}`, {
      method,
      headers: { "Content-Type": "application/json", ...headers },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!res.ok) {
      let detail = `${method} ${path} failed (${res.status})`;
      try {
        const j = await res.json();
        if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
      } catch {
        /* keep default */
      }
      throw new Error(detail);
    }
    try {
      return (await res.json()).response?.data;
    } catch {
      return undefined;
    }
  }, []);

  const fetchPos = useCallback(async (): Promise<any[]> => {
    const list = await getJson("/permission-object");
    setPos(list);
    return list;
  }, [getJson]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [poList, svc, pogList, roleList, rpm] = await Promise.all([
          getJson("/permission-object"),
          getJson("/service"),
          getJson("/permission-object-group"),
          getJson("/role"),
          getJson("/role-pog-mapping"),
        ]);
        setServiceList(svc);
        setPos(poList);
        setPogs(pogList);
        setRoles(roleList.filter((r: any) => isAmpEntitlementDomain(r.domain)));
        setRoleMappings(rpm);
      } catch (err) {
        setError({ title: "Failed to Fetch Data", details: formatEntitlementsError(err) });
      } finally {
        setLoading(false);
      }
    })();
  }, [getJson]);

  // Method access and group membership for the selected object.
  useEffect(() => {
    setMethodAccess(null);
    setPoGroupIds([]);
    if (!selected) return;
    let cancelled = false;
    (async () => {
      try {
        const maps = await getJson(`/pog-po-mapping/po/${selected.permission_object_id}`);
        if (!cancelled) setPoGroupIds(maps.map((m: any) => m.permission_object_group_id));
        if (selected.po_type_code === "API") {
          const ma = await getMethodAccess(selected.permission_object_id);
          if (!cancelled) {
            setMethodAccess(
              ma.entries.length > 0 ? fromEntries(ma.entries) : defaultMethodAccess("API")
            );
          }
        }
      } catch (err) {
        if (!cancelled) setError({ title: "Failed to Load Details", details: formatEntitlementsError(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, getJson]);

  // This tab manages only the amp and mobile domains.
  const visiblePos = useMemo(() => filterByServiceDomain(pos, serviceList), [pos, serviceList]);
  const services: string[] = useMemo(
    () =>
      (filterByServiceDomain(serviceList || [], serviceList) as any[])
        .map((s) => s.service_name)
        .filter(Boolean),
    [serviceList],
  );

  const usedBy: UsedBy = useMemo(() => {
    const pogName = (id: number) =>
      pogs.find((p) => p.permission_object_group_id === id)?.pog_name ?? `#${id}`;
    const roleName = (id: number) => roles.find((r) => r.role_id === id)?.role_name ?? `#${id}`;
    return {
      groups: poGroupIds.map(pogName),
      roles: roleMappings
        .filter(
          (m) =>
            poGroupIds.includes(m.permission_object_group_id) &&
            roles.some((r) => r.role_id === m.role_id),
        )
        .map((m) => ({
          role: roleName(m.role_id),
          group: pogName(m.permission_object_group_id),
          access: m.access_level_name,
        })),
    };
  }, [poGroupIds, pogs, roles, roleMappings]);

  // Unsaved-changes dialog shared by the tab switch guard and row selection.
  const confirmDiscard = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        if (!(isFormDirty || isCreateMode)) return resolve(true);
        setDialog({
          open: true,
          onConfirm: () => {
            setIsFormDirty(false);
            setIsCreateMode(false);
            setDialog(null);
            resolve(true);
          },
          onCancel: () => {
            setDialog(null);
            resolve(false);
          },
        });
      }),
    [isFormDirty, isCreateMode]
  );

  useEffect(() => {
    if (registerTabSwitchGuard) registerTabSwitchGuard(confirmDiscard);
  }, [confirmDiscard, registerTabSwitchGuard]);

  const [formKey, setFormKey] = useState(0);
  const handleRowSelect = async (po: any) => {
    if (!(await confirmDiscard())) return;
    // A form still in edit or create mode is bound to the old object: remount it
    // so it never saves the old values onto the newly selected one.
    if (formMode === "edit" || formMode === "create") {
      setFormKey((k) => k + 1);
      setFormMode(null);
      setIsFormDirty(false);
      setIsCreateMode(false);
    }
    setSelected(po);
  };
  const handleCreate = async (
    payload: PoPayload,
    grid: MethodAccessGridValue
  ): Promise<false | "created" | "partial"> => {
    setLoading(true);
    let created: any;
    try {
      created = await send("POST", "/permission-object", {
        ...payload,
        sys_created_by: actor,
        sys_modified_by: actor,
      });
    } catch (err) {
      setError({ title: "Failed to Create", details: formatEntitlementsError(err) });
      setLoading(false);
      return false;
    }
    let methodError: unknown = null;
    const leaveCreateMode = () => {
      setFormKey((k) => k + 1);
      setFormMode(null);
      setIsFormDirty(false);
      setIsCreateMode(false);
    };
    if (payload.po_type_code === "API" && created?.permission_object_id) {
      try {
        await putMethodAccess(created.permission_object_id, toEntries(grid));
      } catch (err) {
        methodError = err;
      }
    }
    try {
      const list = await fetchPos();
      if (methodError) {
        const match =
          list.find((p) => p.permission_object_id === created?.permission_object_id) ||
          list.find((p) => p.po_type_code === payload.po_type_code && p.po_path === payload.po_path);
        if (match) setSelected(match);
        leaveCreateMode();
        setError({
          title: "Method Access Not Saved",
          details: `The permission object was created, but its method access failed to save (${formatEntitlementsError(
            methodError
          )}). Select it and edit it to set method access.`,
        });
        return "partial";
      }
      return "created";
    } catch (err) {
      if (methodError) leaveCreateMode();
      setError({ title: "Failed to Refresh", details: formatEntitlementsError(err) });
      return methodError ? "partial" : "created";
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id: number, payload: PoPayload, grid: MethodAccessGridValue) => {
    setLoading(true);
    let updated: any;
    try {
      updated = await send("PUT", `/permission-object/${id}`, {
        ...payload,
        sys_modified_by: actor,
      });
    } catch (err) {
      setError({ title: "Failed to Update", details: formatEntitlementsError(err) });
      setLoading(false);
      return false;
    }
    let methodError: unknown = null;
    if (payload.po_type_code === "API") {
      try {
        await putMethodAccess(id, toEntries(grid));
      } catch (err) {
        methodError = err;
      }
    }
    try {
      await fetchPos();
    } catch (err) {
      setError({ title: "Failed to Refresh", details: formatEntitlementsError(err) });
    }
    setSelected((prev: any) => ({ ...prev, ...payload, ...(updated || {}) }));
    if (methodError) {
      setError({
        title: "Method Access Not Saved",
        details: `The permission object was saved, but its method access failed to save (${formatEntitlementsError(
          methodError
        )}).`,
      });
    }
    setLoading(false);
    return true;
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    try {
      await send("DELETE", `/permission-object/${id}`);
      setSelected(null);
      setIsFormDirty(false);
      setIsCreateMode(false);
      await fetchPos();
    } catch (err) {
      setError({ title: "Failed to Delete", details: formatEntitlementsError(err) });
    } finally {
      setLoading(false);
    }
  };

  const split = useResizableSplit("xl");
  return (
    <div
      ref={split.containerRef}
      style={split.containerStyle}
      className={`flex flex-col xl:flex-row xl:items-stretch bg-gray-100 p-4 min-h-full gap-4 ${split.containerClassName}`}
    >
      <div className={`admin-tab-panel flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white relative ${split.leftClassName}`}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
            <LoadingStyle />
          </div>
        )}
        <PermissionObjectTable
          permissionObjects={visiblePos}
          services={services}
          selectedRow={selected?.permission_object_id ?? null}
          onRowSelect={handleRowSelect}
        />
      </div>

      <SplitDivider {...split.dividerProps} />
      <div className="admin-tab-panel admin-tab-form-panel flex-grow flex flex-col lg:basis-1/4 xl:self-stretch bg-white rounded-xl shadow-md">
        <PermissionObjectForm
          key={formKey}
          selectedPo={selected}
          methodAccess={methodAccess}
          usedBy={usedBy}
          services={services}
          permissionObjects={visiblePos}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onDirtyChange={setIsFormDirty}
          onModeChange={(mode) => {
            setFormMode(mode);
            setIsCreateMode(mode === "create");
          }}
          canDelete={canDelete}
        />
      </div>

      {dialog?.open && (
        <Dialog
          title="Unsaved Changes"
          message="You have unsaved changes. Are you sure you want to discard them?"
          confirmButtonText="Yes"
          cancelButtonText="No"
          onConfirm={dialog.onConfirm}
          onCancel={dialog.onCancel}
          onClose={dialog.onCancel}
        />
      )}

      {error && (
        <ErrorDialog title={error.title} details={error.details} onClose={() => setError(null)} />
      )}
    </div>
  );
};

export default PermissionObjectSetup;
